import { useState, useEffect, useCallback } from 'react';
import { Phone, User, MapPin, Building2, Loader2, Minus, Plus, Tag, X, Banknote, Truck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatPrice, cn } from '../lib/utils';
import { toast } from 'sonner';
import { effectiveUnitPrice, hasActiveDiscount } from '../utils/pricing';
import { trackPurchase, trackCheckoutStart } from '../lib/tracker';
import { useSiteSettings, applyFreeShipping } from '../lib/siteSettings';

const API_URL = '';
const PHONE_REGEX = /^(0[567]\d{8}|\+213[567]\d{8}|213[567]\d{8})$/;

/**
 * Inline COD order form — Arabic RTL — embedded directly on the product page.
 * Replaces the "Acheter maintenant" modal with an always-visible form card.
 * Posts to /api/cod-order (same backend contract as CodOrderForm).
 */
export default function InlineCodOrderForm({ product, selectedSize, selectedColor, quantity, setQuantity }) {
  const navigate = useNavigate();
  const { settings } = useSiteSettings();
  const [wilayas, setWilayas] = useState([]);
  const [communes, setCommunes] = useState([]);
  const [ecoFees, setEcoFees] = useState(null);
  const [loadingCommunes, setLoadingCommunes] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [appliedPromo, setAppliedPromo] = useState(null);

  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: '',
    wilaya: '',
    commune: '',
    delivery_type: 'home',
    shipping_address: '',
    website: '', // honeypot
  });

  // Load wilayas
  useEffect(() => {
    fetch(`${API_URL}/api/wilayas`).then(r => r.json()).then(d => setWilayas(d.wilayas || [])).catch(() => {});
  }, []);

  // Selected variant + pricing
  const selectedVariant = (product.variants || []).find(
    v => v.size === selectedSize && (!selectedColor || !v.color || v.color === selectedColor)
  ) || null;
  const productPrice = effectiveUnitPrice(product, selectedVariant);
  const subtotal = productPrice * quantity;

  const selectedWilaya = wilayas.find(w => w.code === formData.wilaya);
  const selectedCommune = communes.find(c => c.nom === formData.commune);
  const hasBureauForCommune = selectedCommune ? selectedCommune.has_stop_desk === 1 : false;

  // Auto-apply affiliate ref code
  const validatePromo = useCallback(async (overrideCode = null) => {
    const codeToApply = overrideCode || promoCode;
    if (!codeToApply || !codeToApply.trim()) return;
    setPromoLoading(true);
    try {
      const affRes = await fetch(`${API_URL}/api/affiliate/validate/${encodeURIComponent(codeToApply.trim().toUpperCase())}`);
      if (affRes.ok) {
        const data = await affRes.json();
        let discount = 0;
        const isOnSale = hasActiveDiscount(product, selectedVariant);
        const isEligible = product.affiliate_eligible !== false;
        if (!isOnSale && isEligible) {
          if (data.discount_type === 'percentage') discount = subtotal * (data.discount_value / 100);
          else discount = Math.min(data.discount_value, subtotal);
        }
        setAppliedPromo({
          code: data.code,
          discount_type: data.discount_type,
          discount_value: data.discount_value,
          discount_amount: Math.round(discount),
          is_affiliate: true,
        });
        if (!overrideCode) {
          if (discount > 0) toast.success(data.message || `Code ${data.code} appliqué`);
          else toast.info('Article en promotion — code non applicable');
        }
        return;
      }
      if (overrideCode) {
        localStorage.removeItem('bloom_affiliate_ref');
        return;
      }
      const promoRes = await fetch(`${API_URL}/api/promo/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: codeToApply.trim(),
          subtotal,
          items: [{ product_id: product.product_id, price: productPrice, quantity }],
        }),
      });
      if (promoRes.ok) {
        const data = await promoRes.json();
        setAppliedPromo({ ...data, is_affiliate: !!data.is_affiliate });
        toast.success(data.message || 'Code appliqué');
      } else {
        const err = await promoRes.json();
        toast.error(err.detail || 'Code invalide');
        setAppliedPromo(null);
      }
    } catch {
      if (!overrideCode) toast.error('Erreur de validation');
    } finally {
      setPromoLoading(false);
    }
  }, [promoCode, subtotal, productPrice, quantity, product, selectedVariant]);

  // On mount, auto-apply ref
  useEffect(() => {
    const urlRef = new URLSearchParams(window.location.search).get('ref');
    const storedRef = localStorage.getItem('bloom_affiliate_ref');
    const refCode = urlRef || storedRef;
    if (urlRef) localStorage.setItem('bloom_affiliate_ref', urlRef);
    if (refCode && !appliedPromo) {
      const codeUpper = refCode.trim().toUpperCase();
      setPromoCode(codeUpper);
      setTimeout(() => validatePromo(codeUpper), 200);
    }
  }, []);

  const loadWilayaData = useCallback(async (wilayaCode) => {
    if (!wilayaCode) { setCommunes([]); setEcoFees(null); return; }
    const numCode = parseInt(wilayaCode, 10);
    setLoadingCommunes(true);
    try {
      const [communesRes, feesRes] = await Promise.all([
        fetch(`${API_URL}/api/ecotrack/communes/${numCode}`).then(r => r.json()),
        fetch(`${API_URL}/api/ecotrack/fees/${numCode}`).then(r => r.json()),
      ]);
      setCommunes(communesRes.communes || []);
      setEcoFees(feesRes);
    } catch {
      setCommunes([]);
      setEcoFees(null);
    } finally {
      setLoadingCommunes(false);
    }
  }, []);

  // Compute shipping cost
  let shippingCost = 0;
  let shippingReady = false;
  if (selectedWilaya) {
    if (ecoFees && (ecoFees.tarif_domicile > 0 || ecoFees.tarif_bureau > 0)) {
      shippingCost = formData.delivery_type === 'bureau' ? ecoFees.tarif_bureau : ecoFees.tarif_domicile;
    } else {
      shippingCost = formData.delivery_type === 'bureau'
        ? (selectedWilaya.bureau_cost || Math.max(0, (selectedWilaya.shipping_cost || 0) - 150))
        : (selectedWilaya.shipping_cost || 0);
    }
    shippingCost = applyFreeShipping(shippingCost, subtotal, settings);
    shippingReady = true;
  }
  const promoDiscount = appliedPromo?.discount_amount || 0;
  const total = Math.max(0, subtotal + shippingCost - promoDiscount);

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: null }));
  };

  const onWilayaChange = (code) => {
    updateField('wilaya', code);
    updateField('commune', '');
    loadWilayaData(code);
  };

  const validate = () => {
    const newErrors = {};
    if (!selectedSize) newErrors._size = 'يرجى اختيار المقاس';
    if (!formData.customer_name || formData.customer_name.trim().length < 2) newErrors.customer_name = 'الإسم مطلوب';
    const phone = formData.customer_phone.replace(/\s|-/g, '');
    if (!PHONE_REGEX.test(phone)) newErrors.customer_phone = 'رقم الهاتف غير صحيح';
    if (!formData.wilaya) newErrors.wilaya = 'اختر ولايتك';
    if (!formData.commune) newErrors.commune = 'اختر البلدية';
    if (formData.delivery_type === 'bureau' && !hasBureauForCommune) {
      newErrors.delivery_type = 'لا يوجد مكتب توصيل في هذه البلدية';
    }
    setErrors(newErrors);
    if (newErrors._size) toast.error('يرجى اختيار المقاس');
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      // Track InitiateCheckout right before posting
      trackCheckoutStart(subtotal, quantity, [product.product_id]);

      const response = await fetch(`${API_URL}/api/cod-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_name: formData.customer_name.trim(),
          customer_phone: formData.customer_phone.replace(/\s|-/g, ''),
          customer_email: null,
          wilaya: formData.wilaya,
          commune: formData.commune,
          delivery_type: formData.delivery_type,
          shipping_address: formData.delivery_type === 'home'
            ? (formData.shipping_address || `${selectedCommune?.nom || ''}, ${selectedWilaya?.name || ''}`)
            : null,
          product_id: product.product_id,
          size: selectedSize,
          color: selectedColor || null,
          quantity,
          promo_code: appliedPromo?.code || null,
          website: formData.website,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        trackPurchase(
          data.order_id,
          data.total,
          data.wilaya,
          data.delivery_type,
          (data.items || []).map(it => ({ id: it.product_id, quantity: it.quantity, item_price: it.price }))
        );
        // Hand off to existing success page
        navigate(`/order-success?order_id=${data.order_id}`, { state: { order: data } });
      } else {
        toast.error(data.detail || 'حدث خطأ، يرجى المحاولة مرة أخرى');
      }
    } catch {
      toast.error('خطأ في الاتصال — يرجى المحاولة مرة أخرى');
    } finally {
      setSubmitting(false);
    }
  };

  const removePromo = () => { setAppliedPromo(null); setPromoCode(''); };

  return (
    <div
      dir="rtl"
      lang="ar"
      className="bg-white border border-[#E0DFDD] shadow-sm rounded-sm overflow-hidden"
      data-testid="inline-cod-form"
    >
      {/* Header */}
      <div className="px-5 py-4 bg-[#F9F8F6] border-b border-[#E0DFDD] text-center">
        <h3 className="font-medium text-lg text-[#1A1514]">إملأ الإستمارة للطلب</h3>
      </div>

      <div className="p-5 space-y-4">
        {/* Honeypot */}
        <input
          type="text"
          name="website"
          value={formData.website}
          onChange={e => updateField('website', e.target.value)}
          className="hidden"
          tabIndex={-1}
          autoComplete="off"
        />

        {/* Phone + Name (2 col) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <div className={cn(
              "flex items-center gap-2 border bg-white px-3 py-2.5",
              errors.customer_phone ? "border-red-400" : "border-[#E0DFDD]"
            )}>
              <Phone size={16} className="text-[#6B6A68] flex-shrink-0" />
              <input
                type="tel"
                value={formData.customer_phone}
                onChange={e => updateField('customer_phone', e.target.value)}
                placeholder="رقم الهاتف"
                className="flex-1 text-sm focus:outline-none bg-transparent text-right"
                data-testid="inline-phone"
              />
            </div>
            {errors.customer_phone && <p className="text-xs text-red-500 mt-1">{errors.customer_phone}</p>}
          </div>
          <div>
            <div className={cn(
              "flex items-center gap-2 border bg-white px-3 py-2.5",
              errors.customer_name ? "border-red-400" : "border-[#E0DFDD]"
            )}>
              <User size={16} className="text-[#6B6A68] flex-shrink-0" />
              <input
                type="text"
                value={formData.customer_name}
                onChange={e => updateField('customer_name', e.target.value)}
                placeholder="الإسم واللقب"
                className="flex-1 text-sm focus:outline-none bg-transparent text-right"
                data-testid="inline-name"
              />
            </div>
            {errors.customer_name && <p className="text-xs text-red-500 mt-1">{errors.customer_name}</p>}
          </div>
        </div>

        {/* Wilaya + Commune (2 col) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <div className={cn(
              "flex items-center gap-2 border bg-white px-3 py-2.5",
              errors.wilaya ? "border-red-400" : "border-[#E0DFDD]"
            )}>
              <MapPin size={16} className="text-[#6B6A68] flex-shrink-0" />
              <select
                value={formData.wilaya}
                onChange={e => onWilayaChange(e.target.value)}
                className="flex-1 text-sm focus:outline-none bg-transparent text-right"
                data-testid="inline-wilaya"
              >
                <option value="">إختر ولايتك</option>
                {wilayas.map(w => (
                  <option key={w.code} value={w.code}>{w.code} — {w.name}</option>
                ))}
              </select>
            </div>
            {errors.wilaya && <p className="text-xs text-red-500 mt-1">{errors.wilaya}</p>}
          </div>
          <div>
            <div className={cn(
              "flex items-center gap-2 border bg-white px-3 py-2.5",
              errors.commune ? "border-red-400" : "border-[#E0DFDD]"
            )}>
              <Building2 size={16} className="text-[#6B6A68] flex-shrink-0" />
              {loadingCommunes ? (
                <div className="flex items-center gap-2 text-sm text-[#6B6A68]">
                  <Loader2 size={14} className="animate-spin" />
                  جار التحميل...
                </div>
              ) : (
                <select
                  value={formData.commune}
                  onChange={e => updateField('commune', e.target.value)}
                  className="flex-1 text-sm focus:outline-none bg-transparent text-right"
                  disabled={!formData.wilaya}
                  data-testid="inline-commune"
                >
                  <option value="">إختر البلدية</option>
                  {(formData.delivery_type === 'bureau'
                    ? communes.filter(c => c.has_stop_desk === 1)
                    : communes
                  ).map(c => (
                    <option key={c.nom} value={c.nom}>
                      {c.nom}{c.has_stop_desk ? ' (مكتب)' : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>
            {errors.commune && <p className="text-xs text-red-500 mt-1">{errors.commune}</p>}
          </div>
        </div>

        {/* Delivery Location radios */}
        <div>
          <div className="text-sm text-[#1A1514] font-medium mb-2">مكان التوصيل</div>
          <div className="space-y-2">
            <label
              className={cn(
                "flex items-center justify-between gap-2 border px-3 py-2.5 cursor-pointer transition-colors",
                formData.delivery_type === 'home' ? "border-[#1A1514] bg-[#F9F8F6]" : "border-[#E0DFDD]"
              )}
              data-testid="inline-delivery-home"
            >
              <span className="text-sm">للمنزل</span>
              <input
                type="radio"
                name="inline_delivery_type"
                checked={formData.delivery_type === 'home'}
                onChange={() => updateField('delivery_type', 'home')}
                className="accent-[#1A1514]"
              />
            </label>
            <label
              className={cn(
                "flex items-center justify-between gap-2 border px-3 py-2.5 cursor-pointer transition-colors",
                formData.delivery_type === 'bureau' ? "border-[#1A1514] bg-[#F9F8F6]" : "border-[#E0DFDD]",
                formData.wilaya && communes.length > 0 && !communes.some(c => c.has_stop_desk === 1) && "opacity-50"
              )}
              data-testid="inline-delivery-bureau"
            >
              <span className="text-sm">لمكتب التوصيل</span>
              <input
                type="radio"
                name="inline_delivery_type"
                checked={formData.delivery_type === 'bureau'}
                onChange={() => {
                  updateField('delivery_type', 'bureau');
                  if (formData.commune && !hasBureauForCommune) updateField('commune', '');
                }}
                className="accent-[#1A1514]"
              />
            </label>
          </div>
          {errors.delivery_type && <p className="text-xs text-red-500 mt-1">{errors.delivery_type}</p>}
        </div>

        {/* Promo Code */}
        <div className="pt-2 border-t border-dashed border-[#E0DFDD]">
          {appliedPromo ? (
            <div className="flex items-center justify-between bg-[#F0F7F0] border border-green-200 px-3 py-2">
              <button onClick={removePromo} className="p-1 hover:bg-red-50 rounded" data-testid="inline-remove-promo">
                <X size={14} className="text-red-500" />
              </button>
              <div className="flex items-center gap-2" dir="ltr">
                <span className="text-xs text-green-600">
                  {appliedPromo.discount_amount > 0 ? `-${formatPrice(appliedPromo.discount_amount)}` : 'Non applicable'}
                </span>
                <span className="font-mono text-sm font-medium text-green-700">{appliedPromo.code}</span>
              </div>
            </div>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={() => validatePromo()}
                disabled={promoLoading || !promoCode.trim()}
                className="px-4 py-2 bg-[#1A1514] text-white text-xs font-medium disabled:opacity-40"
                data-testid="inline-apply-promo"
              >
                {promoLoading ? <Loader2 size={14} className="animate-spin" /> : 'تطبيق'}
              </button>
              <input
                type="text"
                value={promoCode}
                onChange={e => setPromoCode(e.target.value.toUpperCase())}
                placeholder="كود الخصم (إختياري)"
                className="flex-1 border border-[#E0DFDD] px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#1A1514] text-right"
                onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), validatePromo())}
                data-testid="inline-promo-input"
              />
              <Tag size={16} className="self-center text-[#6B6A68] flex-shrink-0" />
            </div>
          )}
        </div>

        {/* Summary */}
        <div className="border-t border-[#E0DFDD] pt-3 space-y-2 text-sm">
          <div className="flex justify-between items-center">
            <span>
              {shippingReady
                ? (shippingCost === 0
                    ? <span className="text-[#D4AF37] font-semibold">مجاني</span>
                    : <span>{shippingCost} دج</span>)
                : <span className="text-xs text-[#6B6A68] bg-[#F9F8F6] px-2 py-0.5">اختر الولاية ومكان التوصيل</span>}
            </span>
            <span className="text-[#1A1514]">سعر التوصيل</span>
          </div>
          <div className="flex justify-between items-center">
            <span>{formatPrice(subtotal)}</span>
            <span className="text-[#1A1514]">سعر المنتجات</span>
          </div>
          {promoDiscount > 0 && (
            <div className="flex justify-between items-center text-green-600">
              <span>- {formatPrice(promoDiscount)}</span>
              <span>الخصم</span>
            </div>
          )}
          <div className="flex justify-between items-center pt-2 border-t border-[#E0DFDD]">
            <span className="text-green-600 font-bold text-base" data-testid="inline-total">{formatPrice(total)}</span>
            <span className="text-[#1A1514] font-semibold">التكلفة الإجمالية</span>
          </div>
        </div>

        {/* Qty stepper + Submit */}
        <div className="flex items-stretch gap-2 pt-2">
          {/* Qty stepper (LTR controls for natural − 1 + reading) */}
          <div className="flex items-center border border-[#E0DFDD] bg-white" dir="ltr">
            <button
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
              className="px-3 py-3 hover:bg-[#F4E3E1] transition-colors"
              data-testid="inline-decrease-qty"
              aria-label="نقص الكمية"
            >
              <Minus size={14} />
            </button>
            <span className="w-10 text-center text-sm font-medium" data-testid="inline-quantity">{quantity}</span>
            <button
              onClick={() => setQuantity(quantity + 1)}
              className="px-3 py-3 hover:bg-[#F4E3E1] transition-colors"
              data-testid="inline-increase-qty"
              aria-label="زيادة الكمية"
            >
              <Plus size={14} />
            </button>
          </div>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 bg-[#1A1514] text-white py-3 text-sm font-bold uppercase tracking-wider flex items-center justify-center gap-2 hover:bg-[#2a2524] transition-colors disabled:opacity-50"
            data-testid="inline-submit-order"
          >
            {submitting ? <Loader2 size={18} className="animate-spin" /> : 'أُطلب الآن'}
          </button>
        </div>

        {/* Reassurance footer */}
        <div className="flex items-center justify-center gap-4 pt-2 text-xs text-[#6B6A68]">
          <div className="flex items-center gap-1">
            <Banknote size={14} className="text-[#1A1514]" />
            <span>الدفع عند الإستلام</span>
          </div>
          <div className="w-px h-3 bg-[#E0DFDD]" />
          <div className="flex items-center gap-1">
            <Truck size={14} className="text-[#D4AF37]" />
            <span>التوصيل 24-48 ساعة</span>
          </div>
        </div>
      </div>
    </div>
  );
}
